interface LevelMetrics {
  leads: number
  revenue: number
  cpl: number
  rpl: number
}

interface CostRevenueByLevelProps {
  data: {
    producido: LevelMetrics
    unico: LevelMetrics
  }
}

/**
 * H1 del roadmap (Fase A #3): coste y revenue por lead en 2 niveles lado a
 * lado — "total producido" (vía campaña [LB], con duplicados) y "único"
 * (vía campaña de cliente directo, sin duplicados) — usando el mismo gasto
 * real de plataforma como numerador del CPL en ambos casos.
 *
 * Falta el 3er nivel que pide H1 ("leads reportados por la plataforma de
 * medios"): ningún CSV importado (Google/Microsoft/Facebook/TikTok) trae
 * leads o conversiones reportadas por la propia plataforma, solo gasto —
 * no hay dato real que mostrar, así que no se fabrica (ver
 * docs/roadmap-dashboard-2.0.md, sección 7).
 */
const CostRevenueByLevel = ({ data }: CostRevenueByLevelProps) => {
  const { producido, unico } = data

  // Con 0 leads, cpl y rpl son 0 por definición (no hay división por
  // cero que hacer) y un margen "€0.00/lead" en verde se leería como un
  // punto de equilibrio real en vez de "no hay datos para este nivel" —
  // se distingue explícitamente para no dar una falsa sensación de éxito.
  const renderMargin = (level: LevelMetrics) => {
    if (level.leads === 0) {
      return <div className="mt-3 text-sm font-semibold text-dashboard-muted">Sin datos</div>
    }
    const margin = level.rpl - level.cpl
    return (
      <div className={`mt-3 text-sm font-semibold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        Margen: €{margin.toFixed(2)}/lead
      </div>
    )
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-bold text-dashboard-text mb-2 flex items-center">
        <span className="mr-2">⚖️</span>
        Coste y Revenue por Nivel
      </h2>
      <p className="text-sm text-dashboard-muted mb-6">
        Mismo gasto real de plataforma, medido sobre el total producido (vía Listbroking, con
        duplicados) y sobre los leads únicos (vía campaña de cliente directo, sin duplicados).
        El nivel "único" es el que refleja el coste real por lead vendible.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-dashboard-border-light p-4">
          <div className="text-sm font-semibold text-dashboard-muted mb-3">
            TOTAL PRODUCIDO (vía LB)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-dashboard-muted">Leads</div>
              <div className="text-lg font-bold text-dashboard-text">{producido.leads.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">Revenue</div>
              <div className="text-lg font-bold text-dashboard-text">€{producido.revenue.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">CPL</div>
              <div className="text-lg font-bold text-dashboard-text">€{producido.cpl.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">RPL</div>
              <div className="text-lg font-bold text-dashboard-text">€{producido.rpl.toFixed(2)}</div>
            </div>
          </div>
          {renderMargin(producido)}
        </div>

        <div className="rounded-xl border-2 border-globo-500 bg-globo-50 p-4">
          <div className="text-sm font-semibold text-globo-700 mb-3">
            ÚNICO (vía cliente directo) — CPL real
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-dashboard-muted">Leads</div>
              <div className="text-lg font-bold text-dashboard-text">{unico.leads.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">Revenue</div>
              <div className="text-lg font-bold text-dashboard-text">€{unico.revenue.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">CPL</div>
              <div className="text-lg font-bold text-dashboard-text">€{unico.cpl.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-dashboard-muted">RPL</div>
              <div className="text-lg font-bold text-dashboard-text">€{unico.rpl.toFixed(2)}</div>
            </div>
          </div>
          {renderMargin(unico)}
        </div>
      </div>
      <p className="text-xs text-dashboard-muted mt-4">
        Falta un 3er nivel ("leads reportados por la plataforma de medios"): ningún CSV importado
        trae leads o conversiones reportadas por Google/Meta/Microsoft/TikTok, solo gasto.
      </p>
    </div>
  )
}

export default CostRevenueByLevel
